/**
 * Role Helpers
 *
 * Utilities for managing conditional role field logic.
 * The role dropdown is only visible for specific clients.
 */

import { ROLE_FIELD_CLIENT_ID } from '../constants';

/**
 * Determine if the role field should be visible for a given client
 *
 * @param {string} clientId - The client ID to check
 * @returns {boolean} True if role field should be visible
 */
export function shouldShowRoleField(clientId) {
  if (!clientId) return false;
  return clientId === ROLE_FIELD_CLIENT_ID;
}

/**
 * Validate if a role value is required for a given client
 *
 * @param {string} clientId - The client ID to check
 * @param {string} role - The role value (optional)
 * @returns {boolean} True if role is valid (either not required or provided)
 */
export function isRoleValid(clientId, role) {
  // If role field is not shown for this client, any value (including null/undefined) is valid
  if (!shouldShowRoleField(clientId)) {
    return true;
  }

  // If role field is shown, it's required (must have a value)
  return Boolean(role && role.trim());
}

/**
 * Get validation error message for role field
 *
 * @param {string} clientId - The client ID to check
 * @param {string} role - The role value (optional)
 * @returns {string|null} Error message or null if valid
 */
export function getRoleError(clientId, role) {
  if (!shouldShowRoleField(clientId)) {
    return null; // Role field not applicable for this client
  }

  if (!role || !role.trim()) {
    return 'Role is required';
  }

  return null;
}

/**
 * Sanitize role value before submission
 * Returns undefined if role field is not applicable for the client
 *
 * @param {string} clientId - The client ID
 * @param {string} role - The role value
 * @returns {string|undefined} Sanitized role or undefined
 */
export function sanitizeRole(clientId, role) {
  if (!shouldShowRoleField(clientId)) {
    return undefined; // Don't include role in submission for clients that don't use it
  }

  return role?.trim() || undefined;
}
